import React, { useEffect, useState } from "react";
import { Alert, DatePicker, Form, Input, InputNumber, Modal, Select } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { centsToEuro, decimalInputProps, euroToCents } from "../workReports/format";
import { changeStatus } from "./api";
import {
  STATUS_FIELDS,
  TERMINAL,
  conditionLabels,
  fieldLabels,
  getPath,
  setPath,
  statusLabels
} from "./labels";

const fieldName = path => path.replace(/\./g, "__");

/**
 * Moves a case to `target`: asks for the fields of every step on the way (forward jumps), and a
 * reason when going back, cancelling or recording a rejection.
 */
const StatusDialog = ({ open, view, target, onClose, onDone }) => {
  const { t, i18n } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState([]);
  const labels = fieldLabels(t);
  const program = view?.program;
  const data = view?.data;
  const pipeline = program?.pipeline || [];
  const current = pipeline.indexOf(data?.status);
  const targetIndex = pipeline.indexOf(target);
  const backwards = targetIndex >= 0 && current >= 0 && targetIndex < current;
  const needsReason = backwards || TERMINAL.includes(target);
  const steps =
    !TERMINAL.includes(target) && targetIndex > current
      ? pipeline.slice(current + 1, targetIndex + 1)
      : [];
  const fields = steps
    .flatMap(status => STATUS_FIELDS[status] || [])
    .filter(f => !f.when || (f.when === "grant" ? program.kind === "grant" : program[f.when]))
    .map(f => ({
      ...f,
      required:
        f.required === true || (typeof f.required === "string" && Boolean(program[f.required]))
    }));

  useEffect(() => {
    if (!open || !data) return;
    setMissing([]);
    const values = { note: "" };
    for (const f of fields) {
      const value = getPath(data, f.path);
      if (f.type === "date")
        values[fieldName(f.path)] = value ? dayjs(value) : f.required ? dayjs() : null;
      else if (f.type === "month") values[fieldName(f.path)] = value ? dayjs(`${value}-01`) : null;
      else if (f.type === "money")
        values[fieldName(f.path)] =
          value === null || value === undefined ? null : centsToEuro(value);
      else values[fieldName(f.path)] = value || undefined;
    }
    form.resetFields();
    form.setFieldsValue(values);
  }, [open, target]);

  const submit = async values => {
    setBusy(true);
    try {
      let body = { status: target, note: values.note || "" };
      for (const f of fields) {
        const raw = values[fieldName(f.path)];
        let value;
        if (f.type === "date") value = raw ? raw.format("YYYY-MM-DD") : "";
        else if (f.type === "month") value = raw ? raw.format("YYYY-MM") : "";
        else if (f.type === "money")
          value = raw === null || raw === undefined || raw === "" ? null : euroToCents(raw);
        else value = raw || "";
        body = setPath(body, f.path, value);
      }
      const res = await changeStatus(data._id, body);
      onDone(res.data);
    } catch (error) {
      if (error?.response?.data?.code === "MISSING") setMissing(error.response.data.details || []);
      else generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const conditions = conditionLabels(t);
  return (
    <Modal
      rootClassName="funding-page"
      open={open}
      title={t("Change status to: {{status}}", { status: statusLabels(t)[target] || target })}
      okText={t("Save")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      {target === "contract" && program?.conditionRequired && (
        <Alert
          className="mb-3"
          type="warning"
          showIcon
          message={t(
            "BEG: the contract may only be signed before the application if it is conditional on the funding approval."
          )}
        />
      )}
      {missing.length > 0 && (
        <Alert
          className="mb-3"
          type="error"
          showIcon
          message={t("Please fill in the required fields first.")}
          description={missing.map(path => labels[path] || path).join(", ")}
        />
      )}
      <Form form={form} layout="vertical" onFinish={submit}>
        {fields.map(f => (
          <Form.Item
            key={f.path}
            name={fieldName(f.path)}
            label={labels[f.path] || f.path}
            rules={f.required ? [{ required: true, message: t("Required") }] : []}
          >
            {f.type === "date" ? (
              <DatePicker className="fu-full" format="DD.MM.YYYY" />
            ) : f.type === "month" ? (
              <DatePicker className="fu-full" picker="month" format="MM.YYYY" />
            ) : f.type === "money" ? (
              <InputNumber
                className="fu-full"
                min={0}
                precision={2}
                addonAfter="€"
                {...decimalInputProps(i18n.language)}
              />
            ) : f.type === "condition" ? (
              <Select
                allowClear={!f.required}
                options={Object.entries(conditions).map(([value, label]) => ({ value, label }))}
              />
            ) : (
              <Input maxLength={100} />
            )}
          </Form.Item>
        ))}
        <Form.Item
          name="note"
          label={needsReason ? t("Reason") : t("Note")}
          rules={needsReason ? [{ required: true, message: t("Please give a reason.") }] : []}
        >
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} maxLength={1000} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default StatusDialog;
